/**
 * Agency Admin Service
 * Handles API calls for agency admin functionality.
 * Uses the shared apiClient (Axios) for auth, CSRF, and credentials.
 * Axios automatically handles FormData Content-Type (multipart boundary).
 */

import apiClient from './apiClient';

class AgencyAdminService {
  async _get(endpoint, params = {}) {
    const { data } = await apiClient.get(endpoint, { params });
    return data;
  }

  async _post(endpoint, body) {
    const { data } = await apiClient.post(endpoint, body);
    return data;
  }

  async _put(endpoint, body) {
    const { data } = await apiClient.put(endpoint, body);
    return data;
  }

  async _delete(endpoint) {
    const { data } = await apiClient.delete(endpoint);
    return data;
  }

  // Dashboard & Analytics
  async getDashboardSummary() {
    return this._get('/api/agency-admin/dashboard/summary');
  }

  async getAgencyMetrics(agencyId, period = '30d') {
    return this._get('/api/agency-admin/dashboard/metrics', { period });
  }

  async getAgentPerformance(agencyId, filters = {}) {
    return this._get('/api/agency-admin/dashboard/agent-performance', filters);
  }

  async getRevenueAnalytics(agencyId, period = '30d') {
    return this._get('/api/agency-admin/dashboard/revenue-analytics', { period });
  }

  async getRecentActivity(agencyId, limit = 10) {
    return this._get('/api/agency-admin/dashboard/recent-activity', { limit });
  }

  async getBusinessIntelligence(agencyId, period = '30d') {
    return this._get('/api/agency-admin/dashboard/business-intelligence', { period });
  }

  // Agent Management
  async getAgents(filters = {}) {
    return this._get('/api/agency-admin/agents', filters);
  }

  async getAgent(agentId) {
    return this._get(`/api/agency-admin/agents/${agentId}`);
  }

  /**
   * Create a new agent for the agency.
   * @throws {Error} with code (e.g., 'VALIDATION_ERROR', 'SEAT_LIMIT_REACHED', 'ADMIN_LIMIT_REACHED')
   */
  async createAgent(agentData) {
    return this._post('/api/agency-admin/agents', agentData);
  }

  async updateAgent(agentId, updates) {
    return this._put(`/api/agency-admin/agents/${agentId}`, updates);
  }

  async deleteAgent(agentId) {
    return this._delete(`/api/agency-admin/agents/${agentId}`);
  }

  async setCommissionStructure(agencyId, structure) {
    return this._post('/api/agency-admin/agents/commission-structure', { agencyId, structure });
  }

  /**
   * Resend password setup email to an agent.
   * @throws {Error} with code (e.g., 'AGENT_NOT_FOUND', 'SEND_FAILED')
   */
  async resendPasswordSetup(agentId) {
    return this._post(`/api/agency-admin/agents/${agentId}/password-setup`);
  }

  /**
   * @deprecated Invites are not implemented. Use createAgent() instead.
   */
  async inviteAgent() {
    console.warn('inviteAgent() is deprecated. Invites do not exist. Use createAgent() instead.');
    throw new Error('Invites are not implemented. Use createAgent() instead.');
  }

  // Financial Management
  async getCommissionData(agencyId, period = '30d') {
    return this._get('/api/agency-admin/financial/commissions', { period });
  }

  async processCommissionPayout(agencyId, payoutData) {
    return this._post('/api/agency-admin/financial/commissions/payout', { agencyId, ...payoutData });
  }

  async getFinancialReports(agencyId, reportType, period = '30d') {
    return this._get(`/api/agency-admin/financial/reports/${reportType}`, { period });
  }

  async getRevenueBreakdown(agencyId, period = '30d') {
    return this._get('/api/agency-admin/financial/revenue-breakdown', { period });
  }

  // Branding & White-labeling
  async getBranding(agencyId) {
    return this._get('/api/agency-admin/branding', { agencyId });
  }

  async updateBranding(agencyId, brandingData) {
    return this._put('/api/agency-admin/branding', { agencyId, ...brandingData });
  }

  async uploadBrandAssets(agencyId, assets) {
    const formData = new FormData();
    Object.keys(assets).forEach((key) => formData.append(key, assets[key]));
    formData.append('agencyId', agencyId);
    // Axios removes Content-Type for FormData so the browser sets the correct
    // multipart/form-data boundary automatically.
    return this._post('/api/agency-admin/branding/assets', formData);
  }

  async configureDomain(agencyId, domainConfig) {
    return this._post('/api/agency-admin/branding/domain', { agencyId, ...domainConfig });
  }

  async getBrandingPreview(agencyId) {
    return this._get('/api/agency-admin/branding/preview', { agencyId });
  }

  // Reports & Analytics
  async generateCustomReport(agencyId, reportConfig) {
    return this._post('/api/agency-admin/reports/custom', { agencyId, ...reportConfig });
  }

  async getReportTemplates(agencyId) {
    return this._get('/api/agency-admin/reports/templates', { agencyId });
  }

  async scheduleReport(agencyId, scheduleConfig) {
    return this._post('/api/agency-admin/reports/schedule', { agencyId, ...scheduleConfig });
  }

  // Agency Settings
  async getAgencySettings(agencyId) {
    return this._get('/api/agency-admin/settings', { agencyId });
  }

  async updateAgencySettings(agencyId, settings) {
    return this._put('/api/agency-admin/settings', { agencyId, ...settings });
  }

  async getSubscriptionInfo(agencyId) {
    return this._get('/api/agency-admin/subscription', { agencyId });
  }

  async updateSubscription(agencyId, planId) {
    return this._put('/api/agency-admin/subscription', { agencyId, planId });
  }

  // Utility
  async exportData(agencyId, dataType, format = 'csv') {
    return this._get(`/api/agency-admin/export/${dataType}`, { format, agencyId });
  }

  async getAgencyStats(agencyId) {
    return this._get('/api/agency-admin/stats', { agencyId });
  }

  async getActivityLog(filters = {}) {
    return this._get('/api/agency-admin/activity-log', filters);
  }
}

export default new AgencyAdminService();
